/**
 * SyncDebugHUD - Developer Overlay for Sync & Connection Monitoring
 *
 * ISSUE_067 Task E: 개발자용 플로팅 패널.
 * 프로덕션에서는 ?debug=1 쿼리 파라미터로 활성화.
 *
 * @module components/dev/SyncDebugHUD
 */

import { useState, useEffect, useCallback } from "react";
import { useHealthStore, type HealthStatus } from "~/core/resilience/healthStore";
import { useSyncStatusStore } from "~/core/store/useSyncStatusStore";
import { useErrorLogStore } from "~/core/store/useErrorLogStore";
import { realtimeManager } from "~/core/realtime/RealtimeManager";
import { hasSyncWorkerClient, isWorkerTokenSynced } from "~/core/sync/workerClient";
import { localDb } from "~/db/client";

// ============================================================================
// HELPERS
// ============================================================================

const STATUS_COLORS: Record<HealthStatus, string> = {
  connected: "#22c55e",
  degraded: "#f59e0b",
  disconnected: "#ef4444",
};

function formatAgo(epochMs: number): string {
  if (!epochMs) return "never";
  const sec = Math.round((Date.now() - epochMs) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  return `${Math.round(sec / 3600)}h ago`;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function SyncDebugHUD() {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Health
  const healthStatus = useHealthStore((s) => s.status);
  const lastRealtimeAt = useHealthStore((s) => s.lastRealtimeEventAt);
  const lastQueryAt = useHealthStore((s) => s.lastQueryOkAt);
  const lastSyncAt = useHealthStore((s) => s.lastSyncOkAt);
  const lastWsHbAt = useHealthStore((s) => s.lastWsHeartbeatAt);
  const uiStatus = useHealthStore((s) => s.uiStatus);

  // Sync
  const syncPhase = useSyncStatusStore((s) => s.phase);
  const pulled = useSyncStatusStore((s) => s.pulled);
  const totalToPull = useSyncStatusStore((s) => s.totalToPull);
  const syncError = useSyncStatusStore((s) => s.error);

  // Errors
  const errorCount = useErrorLogStore((s) => s.errors.length);

  // Realtime channels
  const [channelCount, setChannelCount] = useState(0);

  // Worker
  const [workerActive, setWorkerActive] = useState(false);
  const [tokenSynced, setTokenSynced] = useState(false);

  const refreshAsync = useCallback(() => {
    localDb.pendingMutations.count().then(setPendingCount).catch(() => {});
    setChannelCount(realtimeManager.getChannelNames().length);
    setWorkerActive(hasSyncWorkerClient());
    setTokenSynced(isWorkerTokenSynced());
  }, []);

  // Polling refresh (2s)
  useEffect(() => {
    refreshAsync();
    const timer = setInterval(refreshAsync, 2000);
    return () => clearInterval(timer);
  }, [refreshAsync]);

  const dot = STATUS_COLORS[healthStatus];

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        style={{
          position: "fixed",
          bottom: 12,
          right: 12,
          zIndex: 99999,
          width: 28,
          height: 28,
          borderRadius: "50%",
          border: `2px solid ${dot}`,
          background: "rgba(0,0,0,0.7)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        title="Sync Debug HUD"
      >
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: dot }} />
      </button>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        bottom: 12,
        right: 12,
        zIndex: 99999,
        width: 280,
        background: "rgba(15,15,15,0.92)",
        color: "#e5e5e5",
        borderRadius: 8,
        fontSize: 11,
        fontFamily: "monospace",
        padding: "8px 10px",
        lineHeight: 1.6,
        backdropFilter: "blur(8px)",
        border: "1px solid rgba(255,255,255,0.1)",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontWeight: 700, fontSize: 12 }}>
          <span style={{ color: dot, marginRight: 6 }}>{"●"}</span>
          Sync Debug
        </span>
        <button
          onClick={() => setIsOpen(false)}
          style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: 14 }}
        >
          {"×"}
        </button>
      </div>

      {/* Health */}
      <Row label="Health" value={healthStatus} color={dot} />
      <Row label="UI" value={uiStatus} color={uiStatus === 'ok' ? '#22c55e' : uiStatus === 'stale_realtime' ? '#f59e0b' : uiStatus === 'offline' ? '#ef4444' : '#3b82f6'} />
      <Row label="Realtime" value={formatAgo(lastRealtimeAt)} />
      <Row label="Query" value={formatAgo(lastQueryAt)} />
      <Row label="Sync" value={formatAgo(lastSyncAt)} />
      <Row label="WS HB" value={formatAgo(lastWsHbAt)} />

      <Divider />

      {/* Sync Status */}
      {syncPhase === "error"
        ? <Row label="Phase" value={syncPhase} color="#ef4444" />
        : <Row label="Phase" value={syncPhase} />}
      {totalToPull > 0 && <Row label="Pull" value={`${pulled}/${totalToPull}`} />}
      {syncError && <Row label="Error" value={syncError} color="#ef4444" />}

      <Divider />

      {/* Infra */}
      {pendingCount > 0
        ? <Row label="Pending Ops" value={String(pendingCount)} color="#f59e0b" />
        : <Row label="Pending Ops" value="0" />}
      <Row label="Channels" value={String(channelCount)} />
      {errorCount > 0
        ? <Row label="Errors" value={String(errorCount)} color="#ef4444" />
        : <Row label="Errors" value="0" />}

      <Divider />

      {/* Worker */}
      <Row label="Worker" value={workerActive ? "active" : "inactive"} color={workerActive ? "#22c55e" : "#888"} />
      <Row label="Token Sync" value={tokenSynced ? "synced" : "pending"} color={tokenSynced ? "#22c55e" : "#f59e0b"} />
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function Row({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span style={{ color: "#888" }}>{label}</span>
      <span style={{ color: color ?? "#e5e5e5" }}>{value}</span>
    </div>
  );
}

function Divider() {
  return <hr style={{ border: "none", borderTop: "1px solid rgba(255,255,255,0.08)", margin: "4px 0" }} />;
}
